import {
	TerraDrawMouseEvent,
	TerraDrawAdapterStyling,
	TerraDrawKeyboardEvent,
	HexColorStyling,
	NumericStyling,
	Cursor,
	UpdateTypes,
	COMMON_PROPERTIES,
	Z_INDEX,
	FinishActions,
	DashArrayStyling,
	DrawInteractions,
	DrawType,
} from "../../common";
import { KeyboardEventKey } from "../../common/keys";
import { CursorValues } from "../../common/cursors";

import {
	BaseModeOptions,
	CustomStyling,
	ModeUpdateOptions,
	TerraDrawBaseDrawMode,
} from "../base.mode";
import { getDefaultStyling } from "../../util/styling";
import {
	FeatureId,
	GeoJSONStoreFeatures,
	StoreValidation,
} from "../../store/store";
import { cartesianDistance } from "../../geometry/measure/pixel-distance";
import { ValidateLineStringFeature } from "../../validations/linestring.validation";
import { MutateFeatureBehavior, Mutations } from "../mutate-feature.behavior";
import { ReadFeatureBehavior } from "../read-feature.behavior";
import { BehaviorConfig } from "../base.behavior";
import { ClosingPointsBehavior } from "../closing-points.behavior";
import { PixelDistanceBehavior } from "../pixel-distance.behavior";
import {
	isDrawInteraction,
	isFiniteNonNegativeNumber,
	isNonNullObject,
	isNull,
	isBoolean,
} from "../../common/checks";

import { CoordinatePointBehavior } from "../select/behaviors/coordinate-point.behavior";

type TerraDrawFreehandLineStringModeKeyEvents = {
	cancel: KeyboardEvent["key"] | null;
	finish: KeyboardEvent["key"] | null;
};

const defaultKeyEvents = {
	cancel: KeyboardEventKey.Escape,
	finish: KeyboardEventKey.Enter,
};

type FreehandLineStringStyling = {
	lineStringWidth: NumericStyling;
	lineStringColor: HexColorStyling;
	lineStringOpacity: NumericStyling;
	lineStringDash: DashArrayStyling;
	coordinatePointWidth: NumericStyling;
	coordinatePointColor: HexColorStyling;
	coordinatePointOpacity: NumericStyling;
	coordinatePointOutlineWidth: NumericStyling;
	coordinatePointOutlineColor: HexColorStyling;
	coordinatePointOutlineOpacity: NumericStyling;
	closingPointColor: HexColorStyling;
	closingPointOpacity: NumericStyling;
	closingPointWidth: NumericStyling;
	closingPointOutlineColor: HexColorStyling;
	closingPointOutlineWidth: NumericStyling;
	closingPointOutlineOpacity: NumericStyling;
};

interface Cursors {
	start?: Cursor;
	close?: Cursor;
}

const defaultCursors = {
	start: CursorValues.Crosshair,
	close: CursorValues.Pointer,
} as Required<Cursors>;

interface TerraDrawFreehandLineStringModeOptions<
	T extends CustomStyling,
> extends BaseModeOptions<T> {
	minDistance?: number;
	keyEvents?: TerraDrawFreehandLineStringModeKeyEvents | null;
	cursors?: Cursors;
	drawInteraction?: DrawInteractions;
	showCoordinatePoints?: boolean;
}

export class TerraDrawFreehandLineStringMode extends TerraDrawBaseDrawMode<FreehandLineStringStyling> {
	mode = "freehand-linestring";

	private canClose = false;
	private showCoordinatePoints = false;
	private currentId: FeatureId | undefined;
	private minDistance: number = 20;
	private keyEvents: TerraDrawFreehandLineStringModeKeyEvents =
		defaultKeyEvents;
	private cursors: Required<Cursors> = defaultCursors;
	private preventNewFeature = false;
	private drawInteraction: DrawInteractions = "click-move";
	private drawType: DrawType | undefined;

	// Behaviors
	private coordinatePoints!: CoordinatePointBehavior;
	private mutateFeature!: MutateFeatureBehavior;
	private readFeature!: ReadFeatureBehavior;
	private pixelDistance!: PixelDistanceBehavior;
	private closingPoints!: ClosingPointsBehavior;

	constructor(
		options?: TerraDrawFreehandLineStringModeOptions<FreehandLineStringStyling>,
	) {
		super(options, true);
		this.updateOptions(options);
	}

	public updateOptions(
		options?: ModeUpdateOptions<
			TerraDrawFreehandLineStringModeOptions<FreehandLineStringStyling>
		>,
	): void {
		super.updateOptions(options);

		if (isBoolean(options?.showCoordinatePoints)) {
			this.showCoordinatePoints = options.showCoordinatePoints;
			this.coordinatePoints?.setEnabled(this.showCoordinatePoints);
		}

		if (isFiniteNonNegativeNumber(options?.minDistance)) {
			this.minDistance = options.minDistance;
		}

		if (isNull(options?.keyEvents)) {
			this.keyEvents = { cancel: null, finish: null };
		} else if (isNonNullObject(options?.keyEvents)) {
			this.keyEvents = { ...this.keyEvents, ...options.keyEvents };
		}

		if (isDrawInteraction(options?.drawInteraction)) {
			this.drawInteraction = options.drawInteraction;
		}

		if (isNonNullObject(options?.cursors)) {
			this.cursors = { ...this.cursors, ...options.cursors };
		}
	}

	private moveDrawAllowed() {
		return (
			this.drawInteraction === "click-move" ||
			this.drawInteraction === "click-move-or-drag"
		);
	}

	private dragDrawAllowed() {
		return (
			this.drawInteraction === "click-drag" ||
			this.drawInteraction === "click-move-or-drag"
		);
	}

	private close() {
		if (this.currentId === undefined) {
			return;
		}

		const updated = this.mutateFeature.updateLineString({
			featureId: this.currentId,
			propertyMutations: {
				[COMMON_PROPERTIES.CURRENTLY_DRAWING]: undefined,
			},
			context: { updateType: UpdateTypes.Finish, action: FinishActions.Draw },
		});

		if (!updated) {
			return;
		}

		if (this.showCoordinatePoints) {
			this.coordinatePoints.createOrUpdate({
				featureId: this.currentId,
				featureCoordinates: updated.geometry.coordinates,
			});
		}

		const featureId = this.currentId;

		this.closingPoints.delete();

		this.canClose = false;
		this.currentId = undefined;
		this.drawType = undefined;

		// Go back to started state
		if (this.state === "drawing") {
			this.setStarted();
		}

		this.onFinish(featureId, {
			mode: this.mode,
			action: FinishActions.Draw,
		});
	}

	/** @internal */
	start() {
		this.setStarted();
		this.setCursor(this.cursors.start);
	}

	/** @internal */
	stop() {
		this.cleanUp();
		this.setStopped();
		this.setCursor(CursorValues.Unset);
	}

	private addCoordinate(event: TerraDrawMouseEvent) {
		if (this.currentId === undefined || this.canClose === false) {
			this.setCursor(this.cursors.start);
			return;
		}

		const [previousLng, previousLat] = this.readFeature.getCoordinate(
			this.currentId,
			-2,
		);
		const { x, y } = this.project(previousLng, previousLat);
		const distance = cartesianDistance(
			{ x, y },
			{ x: event.containerX, y: event.containerY },
		);

		const [closingLng, closingLat] = this.readFeature.getCoordinate(
			this.currentId,
			-1,
		);
		const { x: closingX, y: closingY } = this.project(closingLng, closingLat);
		const closingDistance = cartesianDistance(
			{ x: closingX, y: closingY },
			{ x: event.containerX, y: event.containerY },
		);

		if (closingDistance < this.pointerDistance) {
			this.setCursor(this.cursors.close);
		} else {
			this.setCursor(this.cursors.start);
		}

		// The cursor must have moved a minimum distance
		// before we add another coordinate
		if (distance < this.minDistance) {
			return;
		}

		const updated = this.mutateFeature.updateLineString({
			featureId: this.currentId,
			coordinateMutations: [
				{
					type: Mutations.InsertAfter,
					index: -1,
					coordinate: [event.lng, event.lat],
				},
			],
			context: { updateType: UpdateTypes.Provisional },
		});

		if (!updated) {
			return;
		}

		if (this.showCoordinatePoints) {
			this.coordinatePoints.createOrUpdate({
				featureId: this.currentId,
				featureCoordinates: updated.geometry.coordinates,
			});
		}

		this.closingPoints.update(updated.geometry.coordinates);
	}

	private beginDrawing(
		event: TerraDrawMouseEvent,
		drawType: DrawType = "click",
	) {
		const { id: createdId, geometry } = this.mutateFeature.createLineString({
			coordinates: [
				[event.lng, event.lat],
				[event.lng, event.lat],
			],
			properties: {
				mode: this.mode,
				[COMMON_PROPERTIES.CURRENTLY_DRAWING]: true,
			},
		});

		this.closingPoints.create(geometry.coordinates);
		this.currentId = createdId;

		if (this.showCoordinatePoints) {
			this.coordinatePoints.createOrUpdate({
				featureId: createdId,
				featureCoordinates: geometry.coordinates,
			});
		}

		this.drawType = drawType;
		this.canClose = true;

		// We could already be in drawing due to updating the existing linestring
		// via afterFeatureUpdated
		if (this.state !== "drawing") {
			this.setDrawing();
		}
	}

	/** @internal */
	onMouseMove(event: TerraDrawMouseEvent) {
		if (!this.moveDrawAllowed() || this.drawType !== "click") {
			return;
		}
		this.addCoordinate(event);
	}

	/** @internal */
	onClick(event: TerraDrawMouseEvent) {
		if (!this.moveDrawAllowed() || this.drawType === "drag") {
			return;
		}

		if (
			(event.button === "right" &&
				this.allowPointerEvent(this.pointerEvents.rightClick, event)) ||
			(event.button === "left" &&
				this.allowPointerEvent(this.pointerEvents.leftClick, event)) ||
			(event.isContextMenu &&
				this.allowPointerEvent(this.pointerEvents.contextMenu, event))
		) {
			if (this.preventNewFeature) {
				return;
			}

			if (this.canClose === false) {
				this.beginDrawing(event);

				return;
			}

			this.close();
		}
	}

	/** @internal */
	onKeyDown() {}

	/** @internal */
	onKeyUp(event: TerraDrawKeyboardEvent) {
		if (event.key === this.keyEvents.cancel) {
			this.cleanUp();
		} else if (event.key === this.keyEvents.finish) {
			if (this.canClose === true) {
				this.close();
			}
		}
	}

	/** @internal */
	onDragStart(
		event: TerraDrawMouseEvent,
		setMapDraggability: (enabled: boolean) => void,
	) {
		if (this.state === "drawing") {
			return;
		}

		if (this.preventNewFeature) {
			return;
		}

		if (
			this.allowPointerEvent(this.pointerEvents.onDragStart, event) &&
			this.dragDrawAllowed()
		) {
			this.beginDrawing(event, "drag");
			setMapDraggability(false);
		}
	}

	/** @internal */
	onDrag(
		event: TerraDrawMouseEvent,
		_setMapDraggability: (enabled: boolean) => void,
	) {
		if (
			this.allowPointerEvent(this.pointerEvents.onDrag, event) &&
			this.dragDrawAllowed() &&
			this.drawType === "drag"
		) {
			this.addCoordinate(event);
		}
	}

	/** @internal */
	onDragEnd(
		event: TerraDrawMouseEvent,
		setMapDraggability: (enabled: boolean) => void,
	) {
		if (
			this.allowPointerEvent(this.pointerEvents.onDragEnd, event) &&
			this.dragDrawAllowed() &&
			this.drawType === "drag"
		) {
			this.preventNewFeature = true;
			setTimeout(() => {
				this.preventNewFeature = false;
			}, 500);

			this.close();
			setMapDraggability(true);
		}
	}

	/** @internal */
	cleanUp() {
		const cleanUpId = this.currentId;

		this.currentId = undefined;
		this.drawType = undefined;
		this.canClose = false;
		if (this.state === "drawing") {
			this.setStarted();
		}

		if (cleanUpId !== undefined && this.showCoordinatePoints) {
			this.coordinatePoints.deletePointsByFeatureIds([cleanUpId]);
		}

		this.mutateFeature.deleteFeatureIfPresent(cleanUpId);
		this.closingPoints.delete();
	}

	/** @internal */
	styleFeature(feature: GeoJSONStoreFeatures): TerraDrawAdapterStyling {
		const styles = { ...getDefaultStyling() };

		if (
			feature.type === "Feature" &&
			feature.geometry.type === "LineString" &&
			feature.properties.mode === this.mode
		) {
			styles.lineStringDash = this.getDashArrayStylingValue(
				this.styles.lineStringDash,
				undefined,
				feature,
			);

			styles.lineStringColor = this.getHexColorStylingValue(
				this.styles.lineStringColor,
				styles.lineStringColor,
				feature,
			);

			styles.lineStringOpacity = this.getNumericStylingValue(
				this.styles.lineStringOpacity,
				styles.lineStringOpacity === undefined ? 1 : styles.lineStringOpacity,
				feature,
			);

			styles.lineStringWidth = this.getNumericStylingValue(
				this.styles.lineStringWidth,
				styles.lineStringWidth,
				feature,
			);

			styles.zIndex = Z_INDEX.LAYER_ONE;

			return styles;
		} else if (
			feature.type === "Feature" &&
			feature.properties.mode === this.mode &&
			feature.geometry.type === "Point" &&
			feature.properties[COMMON_PROPERTIES.COORDINATE_POINT]
		) {
			styles.pointWidth = this.getNumericStylingValue(
				this.styles.coordinatePointWidth,
				styles.pointWidth,
				feature,
			);
			styles.pointColor = this.getHexColorStylingValue(
				this.styles.coordinatePointColor,
				styles.pointColor,
				feature,
			);
			styles.pointOpacity = this.getNumericStylingValue(
				this.styles.coordinatePointOpacity,
				1,
				feature,
			);
			styles.pointOutlineWidth = this.getNumericStylingValue(
				this.styles.coordinatePointOutlineWidth,
				2,
				feature,
			);
			styles.pointOutlineColor = this.getHexColorStylingValue(
				this.styles.coordinatePointOutlineColor,
				styles.pointOutlineColor,
				feature,
			);
			styles.pointOutlineOpacity = this.getNumericStylingValue(
				this.styles.coordinatePointOutlineOpacity,
				1,
				feature,
			);
			styles.zIndex = Z_INDEX.LAYER_TWO;

			return styles;
		} else if (
			feature.type === "Feature" &&
			feature.geometry.type === "Point" &&
			feature.properties.mode === this.mode
		) {
			styles.pointWidth = this.getNumericStylingValue(
				this.styles.closingPointWidth,
				styles.pointWidth,
				feature,
			);

			styles.pointOpacity = this.getNumericStylingValue(
				this.styles.closingPointOpacity,
				1,
				feature,
			);

			styles.pointColor = this.getHexColorStylingValue(
				this.styles.closingPointColor,
				styles.pointColor,
				feature,
			);

			styles.pointOutlineColor = this.getHexColorStylingValue(
				this.styles.closingPointOutlineColor,
				styles.pointOutlineColor,
				feature,
			);

			styles.pointOutlineOpacity = this.getNumericStylingValue(
				this.styles.closingPointOutlineOpacity,
				1,
				feature,
			);

			styles.pointOutlineWidth = this.getNumericStylingValue(
				this.styles.closingPointOutlineWidth,
				2,
				feature,
			);

			styles.zIndex = Z_INDEX.LAYER_FIVE;

			return styles;
		}

		return styles;
	}

	validateFeature(feature: unknown): StoreValidation {
		return this.validateModeFeature(feature, (baseValidatedFeature) =>
			ValidateLineStringFeature(baseValidatedFeature, this.coordinatePrecision),
		);
	}

	afterFeatureUpdated(feature: GeoJSONStoreFeatures) {
		if (this.showCoordinatePoints && feature.geometry.type === "LineString") {
			this.coordinatePoints.createOrUpdate({
				featureId: feature.id as FeatureId,
				featureCoordinates: feature.geometry.coordinates,
			});
		}

		// NOTE: This handles the case we are currently drawing a linestring
		// We need to reset the drawing state because it is very complicated (impossible?)
		// to recover the drawing state after a feature update
		if (this.currentId === feature.id) {
			this.closingPoints.delete();
			this.canClose = false;
			this.currentId = undefined;
			this.drawType = undefined;
		}
	}

	afterFeatureAdded(feature: GeoJSONStoreFeatures): void {
		if (this.showCoordinatePoints && feature.geometry.type === "LineString") {
			this.coordinatePoints.createOrUpdate({
				featureId: feature.id as FeatureId,
				featureCoordinates: feature.geometry.coordinates,
			});
		}
	}

	registerBehaviors(config: BehaviorConfig) {
		this.readFeature = new ReadFeatureBehavior(config);
		this.mutateFeature = new MutateFeatureBehavior(config, {
			validate: this.validate,
		});
		this.coordinatePoints = new CoordinatePointBehavior(
			config,
			this.readFeature,
			this.mutateFeature,
		);
		this.pixelDistance = new PixelDistanceBehavior(config);
		this.closingPoints = new ClosingPointsBehavior(
			config,
			this.pixelDistance,
			this.mutateFeature,
			this.readFeature,
		);
	}
}
